import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Collapse,
  TextField,
  Chip,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { ConditionIndicator, ConditionSelector } from './ConditionIndicator';
import { PhotoCapture } from './PhotoCapture';
import { MeasurementInput, MeasurementBadge } from './MeasurementInput';
import { MultiMeasurementGrid } from './MultiMeasurementGrid';
import { AddRecommendationDialog } from './AddRecommendationDialog';
import { estimateRepairCost, formatPrice } from '@/utils/costEstimation';
import type { InspectionItem, InspectionItemCondition, InspectionPhoto, MeasurementValueEntry } from '@/types';
import type { RecommendationUrgency } from '@/hooks/useInspections';

interface InspectionItemRowProps {
  item: InspectionItem;
  editable?: boolean;
  onConditionChange?: (itemId: string, condition: InspectionItemCondition) => void;
  onNotesChange?: (itemId: string, notes: string) => void | Promise<void>;
  onMeasurementChange?: (itemId: string, value: number | null) => void;
  onMeasurementValuesChange?: (itemId: string, values: MeasurementValueEntry[]) => void;
  onAddPhoto?: (itemId: string, file: File) => Promise<void>;
  onDeletePhoto?: (itemId: string, photoId: string) => Promise<void>;
  onAddRecommendation?: (
    itemId: string,
    recommendation: {
      description: string;
      customerDescription?: string;
      urgency: RecommendationUrgency;
      partsCostCents: number;
      laborCostCents: number;
      laborHours?: number;
    }
  ) => Promise<void>;
  photos?: InspectionPhoto[];
  isUploadingPhoto?: boolean;
  isSavingItem?: boolean;
  isAddingRecommendation?: boolean;
  vehicleMake?: string;
  vehicleModel?: string;
}

export function InspectionItemRow({
  item,
  editable = false,
  onConditionChange,
  onNotesChange,
  onMeasurementChange,
  onMeasurementValuesChange,
  onAddPhoto,
  onDeletePhoto,
  onAddRecommendation,
  photos: externalPhotos,
  isUploadingPhoto,
  isSavingItem = false,
  isAddingRecommendation,
  vehicleMake,
  vehicleModel,
}: InspectionItemRowProps) {
  const { t } = useTranslation('inspection');
  const { t: tc } = useTranslation('common');
  const [expanded, setExpanded] = useState(false);
  const [localNotes, setLocalNotes] = useState(item.notes ?? '');
  const [notesError, setNotesError] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const pendingNotes = useRef(0);
  const lastSubmittedNotes = useRef<string | null>(null);
  const notesSaveVersion = useRef(0);
  const previousNotes = useRef(item.notes ?? '');
  useEffect(() => {
    const before = previousNotes.current;
    const incoming = item.notes ?? '';
    setLocalNotes((current) => current === before ? incoming : current);
    previousNotes.current = incoming;
  }, [item.id, item.notes]);
  const [recommendationDialogOpen, setRecommendationDialogOpen] = useState(false);

  const hasIssue = item.condition === 'poor' || item.condition === 'critical';
  const hasRecommendation = !!item.recommendation;

  // Calculate cost estimate based on condition
  const costEstimate = item.condition ? estimateRepairCost(item.name, item.condition) : null;

  const handleNotesBlur = async () => {
    if (!editable || !onNotesChange) return;
    if (pendingNotes.current > 0 && lastSubmittedNotes.current === localNotes) return;
    if (pendingNotes.current === 0 && !notesError && localNotes === (item.notes ?? '')) return;
    const version = ++notesSaveVersion.current;
    const submittedNotes = localNotes;
    pendingNotes.current += 1;
    lastSubmittedNotes.current = submittedNotes;
    setNotesSaving(true);
    setNotesError(false);
    try {
      await onNotesChange(item.id, submittedNotes);
      previousNotes.current = submittedNotes;
    } catch {
      if (version === notesSaveVersion.current) setNotesError(true);
    } finally {
      pendingNotes.current -= 1;
      if (version === notesSaveVersion.current) setNotesSaving(false);
    }
  };

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 },
      }}
    >
      {/* Main Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 1.5,
          px: 2,
          backgroundColor: hasIssue ? 'error.50' : 'transparent',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Condition Indicator */}
        <Box sx={{ width: 32 }}>
          <ConditionIndicator condition={item.condition} size="medium" />
        </Box>

        {/* Item Name */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={hasIssue ? 600 : 400}>
            {item.name}
          </Typography>
          {item.description && (
            <Typography variant="caption" color="text.secondary">
              {item.description}
            </Typography>
          )}
        </Box>

        {/* Indicators */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Measurement Badge — multi-measurement shows count */}
          {item.measurementValues && item.measurementValues.length > 0 && (
            <Chip
              size="small"
              label={`${item.measurementValues.length} ${t('item.measurementValues')}`}
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          )}
          {/* Single measurement badge (legacy) */}
          {!(item.measurementValues && item.measurementValues.length > 0) && item.hasMeasurement && item.measurementValue !== undefined && item.measurementUnit && (
            <MeasurementBadge
              value={item.measurementValue}
              unit={item.measurementUnit}
              min={item.measurementMin}
            />
          )}
          {item.photos.length > 0 && (
            <Chip
              size="small"
              icon={<PhotoCameraIcon sx={{ fontSize: 14 }} />}
              label={item.photos.length}
              sx={{ height: 22 }}
            />
          )}
          {hasRecommendation && (
            <Chip
              size="small"
              color={item.recommendation!.urgency === 'immediate' ? 'error' : 'warning'}
              label={item.recommendation!.urgency === 'immediate' ? t('item.urgent') : t('item.recommendation')}
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          )}
        </Box>

        {/* Expand Button */}
        <Tooltip title={expanded ? tc('collapse') : tc('expand')}>
          <IconButton size="small" aria-label={expanded ? tc('collapse') : tc('expand')} sx={{ minWidth: 44, minHeight: 44 }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Expanded Content */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2, pt: 1, backgroundColor: 'background.default' }}>
          {/* Condition Selector (if editable) */}
          {editable && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                {t('item.condition')}
              </Typography>
              <ConditionSelector
                value={item.condition}
                onChange={(condition) => onConditionChange?.(item.id, condition)}
              />
            </Box>
          )}

          {/* Multi-Measurement Grid */}
          {item.measurementsConfig && item.measurementsConfig.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                {t('item.measurements')}
              </Typography>
              <MultiMeasurementGrid
                subItems={item.subItemsConfig ?? []}
                measurements={item.measurementsConfig}
                values={item.measurementValues ?? []}
                onChange={
                  editable && onMeasurementValuesChange
                    ? (values) => onMeasurementValuesChange(item.id, values)
                    : undefined
                }
                disabled={!editable || isSavingItem}
              />
            </Box>
          )}

          {/* Single Measurement Input (legacy) */}
          {item.hasMeasurement && item.measurementUnit && !(item.measurementsConfig && item.measurementsConfig.length > 0) && (
            <Box sx={{ mb: 2 }}>
              <MeasurementInput
                value={item.measurementValue}
                unit={item.measurementUnit}
                onChange={
                  editable && onMeasurementChange
                    ? (value) => onMeasurementChange(item.id, value)
                    : undefined
                }
                disabled={!editable || isSavingItem}
                min={item.measurementMin}
                max={item.measurementMax}
                label={t('item.measurement')}
              />
            </Box>
          )}

          {/* Notes */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              {t('item.notes')}
            </Typography>
            {editable ? (
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                placeholder={t('item.addNotePlaceholder')}
                value={localNotes}
                onChange={(e) => setLocalNotes(e.target.value)}
                onBlur={() => { void handleNotesBlur(); }}
                error={notesError}
                helperText={notesError ? t('detail.notesSaveFailed') : notesSaving ? t('detail.saving') : t('detail.notesSaveOnBlur')}
                inputProps={{ maxLength: 1000, 'aria-label': t('item.notes') }}
              />
            ) : (
              <Typography variant="body2">
                {item.notes || t('item.noNotes')}
              </Typography>
            )}
          </Box>

          {/* Photos */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              {t('item.photos')}
            </Typography>
            {onAddPhoto ? (
              <PhotoCapture
                photos={(externalPhotos ?? item.photos).map((photo) => ({
                  id: photo.id,
                  // Handle both mock data (url) and API data (fileUrl)
                  url: 'fileUrl' in photo ? (photo as { fileUrl: string }).fileUrl : photo.url,
                  thumbnailUrl: photo.thumbnailUrl,
                  caption: photo.caption,
                }))}
                onCapture={editable ? (file) => onAddPhoto(item.id, file) : undefined}
                onDelete={
                  editable && onDeletePhoto
                    ? (photoId) => onDeletePhoto(item.id, photoId)
                    : undefined
                }
                disabled={!editable || isUploadingPhoto}
                maxPhotos={10}
                itemName={item.name}
              />
            ) : (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {item.photos.map((photo) => (
                  <Box
                    key={photo.id}
                    component="img"
                    src={photo.thumbnailUrl}
                    alt={photo.caption ?? t('item.inspectionPhoto')}
                    sx={{
                      width: 80,
                      height: 80,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                    }}
                  />
                ))}
                {editable && (
                  <Button
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    disabled
                    sx={{ height: 80, minWidth: 80 }}
                  >
                    {tc('add')}
                  </Button>
                )}
                {!editable && item.photos.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {t('item.noPhotos')}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Cost Estimate */}
          {costEstimate && !hasRecommendation && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                backgroundColor: 'info.50',
                border: 1,
                borderColor: 'info.200',
              }}
            >
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                {t('item.costEstimateAutomatic')}
              </Typography>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                {costEstimate.description}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('item.labor')}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatPrice(costEstimate.laborCents)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('item.parts')}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatPrice(costEstimate.partsCents)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('item.total')}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="primary.main">
                    {formatPrice(costEstimate.totalCents)}
                  </Typography>
                </Box>
              </Box>
              <Chip
                size="small"
                label={
                  costEstimate.confidence === 'high'
                    ? t('item.accurateEstimate')
                    : costEstimate.confidence === 'medium'
                      ? t('item.approximateEstimate')
                      : t('item.roughEstimate')
                }
                sx={{ mt: 1, fontSize: '0.65rem' }}
                color={costEstimate.confidence === 'high' ? 'success' : 'default'}
              />
            </Box>
          )}

          {/* Recommendation */}
          {hasRecommendation && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                backgroundColor:
                  item.recommendation!.urgency === 'immediate'
                    ? 'error.50'
                    : 'warning.50',
              }}
            >
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                {t('item.recommendation')}
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {item.recommendation!.description}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Chip
                  size="small"
                  label={
                    item.recommendation!.urgency === 'immediate'
                      ? t('item.urgent')
                      : item.recommendation!.urgency === 'soon'
                        ? t('item.soon')
                        : t('item.future')
                  }
                  color={item.recommendation!.urgency === 'immediate' ? 'error' : 'warning'}
                />
                <Typography variant="body2" fontWeight={600}>
                  €{(item.recommendation!.estimatedPriceCents / 100).toFixed(2)}
                </Typography>
              </Box>
              {item.recommendation!.approved !== undefined && (
                <Chip
                  size="small"
                  label={item.recommendation!.approved ? t('item.approved') : t('item.rejected')}
                  color={item.recommendation!.approved ? 'success' : 'default'}
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          )}

          {/* Add Recommendation Button (if editable and has issue) */}
          {editable && hasIssue && !hasRecommendation && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<NoteAddIcon />}
              sx={{ mt: 1 }}
              onClick={(e) => {
                e.stopPropagation();
                setRecommendationDialogOpen(true);
              }}
              disabled={!onAddRecommendation}
            >
              {t('item.addRecommendation')}
            </Button>
          )}
        </Box>
      </Collapse>

      {/* Add Recommendation Dialog */}
      {onAddRecommendation && (
        <AddRecommendationDialog
          open={recommendationDialogOpen}
          onClose={() => setRecommendationDialogOpen(false)}
          onSave={async (recommendation) => {
            await onAddRecommendation(item.id, recommendation);
          }}
          item={{
            id: item.id,
            name: item.name,
            categoryId: item.categoryId,
            condition: item.condition as 'good' | 'fair' | 'poor' | 'critical' | null,
          }}
          vehicleMake={vehicleMake}
          vehicleModel={vehicleModel}
          isLoading={isAddingRecommendation}
        />
      )}
    </Box>
  );
}
